import { useState } from 'react';

import ClearAllIcon from '@mui/icons-material/ClearAll';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Slider,
	Stack,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';

import { useClipboardComponent, useCopyToClipboard } from '@@common';

import { ComponentLayoutModel } from '../../Models/ComponentLayoutModel';

type LayoutInputDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	layout: ComponentLayoutModel | undefined;
	onLayoutChange: (layout: ComponentLayoutModel | undefined) => void;
};

const DISPLAY_OPTIONS = ['flex', 'block', 'inline', 'inline-block', 'grid', 'inline-flex', 'none'];
const FLEX_DIRECTION_OPTIONS = ['row', 'row-reverse', 'column', 'column-reverse'];
const ALIGN_OPTIONS = ['flex-start', 'flex-end', 'center', 'stretch', 'baseline'];
const JUSTIFY_OPTIONS = ['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'space-evenly'];
const BORDER_STYLE_OPTIONS = ['none', 'solid', 'dashed', 'dotted', 'double', 'groove', 'ridge', 'inset', 'outset'];
const WHITE_SPACE_OPTIONS = ['normal', 'nowrap', 'pre', 'pre-wrap', 'pre-line', 'break-spaces'];
const FONT_WEIGHT_OPTIONS = ['100', '200', '300', '400', '500', '600', '700', '800', '900', 'normal', 'bold', 'bolder', 'lighter'];
const OVERFLOW_OPTIONS = ['visible', 'hidden', 'scroll', 'auto', 'clip'];
const BOX_SHADOW_OPTIONS = [
	{ label: 'None', value: 'none' },
	{ label: 'Subtle (elevation 1)', value: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)' },
	{ label: 'Standard (elevation 2)', value: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)' },
	{ label: 'Elevated (elevation 3)', value: '0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)' },
	{ label: 'High (elevation 4)', value: '0 14px 28px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22)' },
	{ label: 'Raised (elevation 5)', value: '0 19px 38px rgba(0,0,0,0.30), 0 15px 12px rgba(0,0,0,0.22)' },
	{ label: 'Card', value: '0 2px 4px rgba(0,0,0,0.1)' },
	{ label: 'Card hover', value: '0 4px 8px rgba(0,0,0,0.15)' },
	{ label: 'Bottom only', value: '0 4px 6px -1px rgba(0,0,0,0.1)' },
	{ label: 'Top only', value: '0 -4px 6px -1px rgba(0,0,0,0.1)' },
	{ label: 'Inset', value: 'inset 0 2px 4px 0 rgba(0,0,0,0.06)' },
];
const COLOR_OPTIONS = [
	'primary.main',
	'primary.light',
	'primary.dark',
	'primary.contrastText',
	'secondary.main',
	'secondary.light',
	'secondary.dark',
	'secondary.contrastText',
	'error.main',
	'error.light',
	'error.dark',
	'error.contrastText',
	'warning.main',
	'warning.light',
	'warning.dark',
	'warning.contrastText',
	'info.main',
	'info.light',
	'info.dark',
	'info.contrastText',
	'success.main',
	'success.light',
	'success.dark',
	'success.contrastText',
	'text.primary',
	'text.secondary',
	'text.disabled',
	'background.default',
	'background.paper',
	'divider',
	'action.active',
	'action.hover',
	'action.selected',
	'action.disabled',
	'action.disabledBackground',
	'grey.50',
	'grey.100',
	'grey.200',
	'grey.300',
	'grey.400',
	'grey.500',
	'grey.600',
	'grey.700',
	'grey.800',
	'grey.900',
	'common.white',
	'common.black',
];

/** Resolve a MUI palette token like "primary.main" to a CSS color string. */
function resolveThemeColor(theme: Theme, token: string): string {
	const parts = token.split('.');
	let node: any = theme.palette;
	for (const part of parts) {
		node = node?.[part];
		if (node === undefined) return '';
	}
	return typeof node === 'string' ? node : '';
}

export function LayoutInputDialog({ open, setOpen, layout, onLayoutChange }: LayoutInputDialogProps) {
	const theme = useTheme();
	const [localLayout, setLocalLayout] = useState<ComponentLayoutModel>(layout || {});

	const copyToClipboard = useCopyToClipboard();
	const clipboardLayout = useClipboardComponent<ComponentLayoutModel & { Type: 'Layout' }>();

	// Determine if colors are custom (hex/rgb) or theme colors
	const isCustomColor = (color: string | undefined) => {
		if (!color) return false;
		return color.startsWith('#') || color.startsWith('rgb') || color.startsWith('rgba');
	};

	const [customColor, setCustomColor] = useState(isCustomColor(layout?.Color));
	const [customBgColor, setCustomBgColor] = useState(isCustomColor(layout?.BackgroundColor));
	const [customBorderColor, setCustomBorderColor] = useState(isCustomColor(layout?.BorderColor));

	const handleChange = (field: keyof ComponentLayoutModel, value: any) => {
		setLocalLayout({ ...localLayout, [field]: value || undefined });
	};

	const handleSave = () => {
		// Clean up undefined values
		const cleanedLayout = Object.entries(localLayout).reduce((acc, [key, value]) => {
			if (value !== undefined && value !== '' && value !== null) {
				(acc as any)[key] = value;
			}
			return acc;
		}, {} as ComponentLayoutModel);

		onLayoutChange(Object.keys(cleanedLayout).length > 0 ? cleanedLayout : undefined);
		setOpen(false);
	};

	const handleCancel = () => {
		setLocalLayout(layout || {});
		setCustomColor(isCustomColor(layout?.Color));
		setCustomBgColor(isCustomColor(layout?.BackgroundColor));
		setOpen(false);
	};

	const handleClear = () => {
		setLocalLayout({});
		setCustomColor(false);
		setCustomBgColor(false);
	};

	const handleCopy = async () => {
		// Add synthetic Type property for clipboard detection
		await copyToClipboard({ ...localLayout, Type: 'Layout' });
	};

	const handlePaste = () => {
		if (clipboardLayout && clipboardLayout.Type === 'Layout') {
			// Strip the Type property before applying
			const { Type: _Type, ...layoutWithoutType } = clipboardLayout;
			setLocalLayout(layoutWithoutType);
			setCustomColor(isCustomColor(layoutWithoutType.Color));
			setCustomBgColor(isCustomColor(layoutWithoutType.BackgroundColor));
			setCustomBorderColor(isCustomColor(layoutWithoutType.BorderColor));
		}
	};

	return (
		<Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
			<DialogTitle>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<span>Configure Style</span>
					<Box>
						<Tooltip title="Clear all layout properties">
							<IconButton onClick={handleClear} size="small" color="warning">
								<ClearAllIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Copy layout to clipboard">
							<IconButton onClick={handleCopy} size="small">
								<ContentCopyIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Paste layout from clipboard">
							<span>
								<IconButton onClick={handlePaste} size="small" disabled={!clipboardLayout || clipboardLayout.Type !== 'Layout'}>
									<ContentPasteIcon fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					</Box>
				</Box>
			</DialogTitle>
			<DialogContent>
				<Stack spacing={3} sx={{ mt: 1 }}>
					{/* Size Section */}
					<Stack spacing={2}>
						<Typography variant="subtitle2" color="text.secondary">
							Size
						</Typography>
						<Stack direction="row" spacing={2}>
							<TextField
								label="Width"
								value={localLayout.Width ?? ''}
								onChange={(e) => handleChange('Width', e.target.value)}
								size="small"
								fullWidth
								placeholder="e.g. 100, 100%, auto"
							/>
							<TextField
								label="Height"
								value={localLayout.Height ?? ''}
								onChange={(e) => handleChange('Height', e.target.value)}
								size="small"
								fullWidth
								placeholder="e.g. 100, 100%, auto"
							/>
						</Stack>
						<Stack direction="row" spacing={2}>
							<TextField
								label="Min Width"
								value={localLayout.MinWidth ?? ''}
								onChange={(e) => handleChange('MinWidth', e.target.value)}
								size="small"
								fullWidth
								placeholder="e.g. 100, 100%, auto"
							/>
							<TextField
								label="Max Width"
								value={localLayout.MaxWidth ?? ''}
								onChange={(e) => handleChange('MaxWidth', e.target.value)}
								size="small"
								fullWidth
								placeholder="e.g. 100, 100%, auto"
							/>
						</Stack>
						<Stack direction="row" spacing={2}>
							<TextField
								label="Min Height"
								value={localLayout.MinHeight ?? ''}
								onChange={(e) => handleChange('MinHeight', e.target.value)}
								size="small"
								fullWidth
								placeholder="e.g. 100, 100%, auto"
							/>
							<TextField
								label="Max Height"
								value={localLayout.MaxHeight ?? ''}
								onChange={(e) => handleChange('MaxHeight', e.target.value)}
								size="small"
								fullWidth
								placeholder="e.g. 100, 100%, auto"
							/>
						</Stack>
						<TextField
							label="Size (Grid)"
							type="number"
							value={localLayout.Size ?? ''}
							onChange={(e) => handleChange('Size', e.target.value ? Number(e.target.value) : undefined)}
							size="small"
							fullWidth
							placeholder="e.g. 6, 12"
							helperText="Grid column size (1-12) for GridContainer children"
						/>
					</Stack>

					{/* Color Section */}
					<Stack spacing={2}>
						<Typography variant="subtitle2" color="text.secondary">
							Color
						</Typography>
						<Box>
							<FormControlLabel
								control={<Checkbox checked={customColor} onChange={(e) => setCustomColor(e.target.checked)} size="small" />}
								label="Custom color (hex)"
							/>
							{customColor ? (
								<TextField
									label="Text Color"
									value={localLayout.Color ?? ''}
									onChange={(e) => handleChange('Color', e.target.value)}
									size="small"
									fullWidth
									placeholder="e.g. #ff0000, rgb(255, 0, 0)"
									helperText="Hex code or CSS color"
								/>
							) : (
								<Autocomplete
									size="small"
									options={COLOR_OPTIONS}
									value={localLayout.Color ?? null}
									onChange={(_event, newValue) => handleChange('Color', newValue ?? undefined)}
									renderInput={(params) => <TextField {...params} label="Text Color" />}
									renderOption={(props, option) => {
										const resolved = resolveThemeColor(theme, option);
										return (
											<Box component="li" {...props} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
												<Box
													sx={{
														width: 16,
														height: 16,
														borderRadius: 0.5,
														border: '1px solid',
														borderColor: 'divider',
														backgroundColor: resolved || option,
														flexShrink: 0,
													}}
												/>
												<span>{option}</span>
											</Box>
										);
									}}
									freeSolo={false}
								/>
							)}
						</Box>
						<Box>
							<FormControlLabel
								control={<Checkbox checked={customBgColor} onChange={(e) => setCustomBgColor(e.target.checked)} size="small" />}
								label="Custom background color (hex)"
							/>
							{customBgColor ? (
								<TextField
									label="Background Color"
									value={localLayout.BackgroundColor ?? ''}
									onChange={(e) => handleChange('BackgroundColor', e.target.value)}
									size="small"
									fullWidth
									placeholder="e.g. #00ff00, rgba(0, 255, 0, 0.5)"
									helperText="Hex code or CSS color"
								/>
							) : (
								<Autocomplete
									size="small"
									options={COLOR_OPTIONS}
									value={localLayout.BackgroundColor ?? null}
									onChange={(_event, newValue) => handleChange('BackgroundColor', newValue ?? undefined)}
									renderInput={(params) => <TextField {...params} label="Background Color" />}
									renderOption={(props, option) => {
										const resolved = resolveThemeColor(theme, option);
										return (
											<Box component="li" {...props} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
												<Box
													sx={{
														width: 16,
														height: 16,
														borderRadius: 0.5,
														border: '1px solid',
														borderColor: 'divider',
														backgroundColor: resolved || option,
														flexShrink: 0,
													}}
												/>
												<span>{option}</span>
											</Box>
										);
									}}
									freeSolo={false}
								/>
							)}
						</Box>
						<FormControl size="small" fullWidth>
							<InputLabel>Font Weight</InputLabel>
							<Select label="Font Weight" value={localLayout.FontWeight ?? ''} onChange={(e) => handleChange('FontWeight', e.target.value)}>
								<MenuItem value="">
									<em>None</em>
								</MenuItem>
								{FONT_WEIGHT_OPTIONS.map((option) => (
									<MenuItem key={option} value={option}>
										{option}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>

					{/* Flex Section */}
					<Stack spacing={2}>
						<Typography variant="subtitle2" color="text.secondary">
							Flexbox
						</Typography>
						<Stack direction="row" spacing={2}>
							<FormControl size="small" fullWidth>
								<InputLabel>Display</InputLabel>
								<Select label="Display" value={localLayout.Display ?? ''} onChange={(e) => handleChange('Display', e.target.value)}>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{DISPLAY_OPTIONS.map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small" fullWidth>
								<InputLabel>Overflow</InputLabel>
								<Select label="Overflow" value={localLayout.Overflow ?? ''} onChange={(e) => handleChange('Overflow', e.target.value)}>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{OVERFLOW_OPTIONS.map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Stack>{' '}
						<Stack direction="row" spacing={2}>
							<FormControl size="small" fullWidth>
								<InputLabel>Overflow X</InputLabel>
								<Select label="Overflow X" value={localLayout.OverflowX ?? ''} onChange={(e) => handleChange('OverflowX', e.target.value)}>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{OVERFLOW_OPTIONS.map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small" fullWidth>
								<InputLabel>Overflow Y</InputLabel>
								<Select label="Overflow Y" value={localLayout.OverflowY ?? ''} onChange={(e) => handleChange('OverflowY', e.target.value)}>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{OVERFLOW_OPTIONS.map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Stack>{' '}
						<Stack direction="row" spacing={2}>
							<FormControl size="small" fullWidth>
								<InputLabel>Flex Direction</InputLabel>
								<Select label="Flex Direction" value={localLayout.FlexDirection ?? ''} onChange={(e) => handleChange('FlexDirection', e.target.value)}>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{FLEX_DIRECTION_OPTIONS.map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Stack>
						<Stack direction="row" spacing={2}>
							<TextField
								label="Flex"
								type="number"
								value={localLayout.Flex ?? ''}
								onChange={(e) => handleChange('Flex', e.target.value ? Number(e.target.value) : undefined)}
								size="small"
								fullWidth
							/>
							<TextField
								label="Flex Grow"
								type="number"
								value={localLayout.FlexGrow ?? ''}
								onChange={(e) => handleChange('FlexGrow', e.target.value ? Number(e.target.value) : undefined)}
								size="small"
								fullWidth
							/>
							<TextField
								label="Flex Shrink"
								type="number"
								value={localLayout.FlexShrink ?? ''}
								onChange={(e) => handleChange('FlexShrink', e.target.value ? Number(e.target.value) : undefined)}
								size="small"
								fullWidth
							/>
						</Stack>
					</Stack>
					{/* Overflow Section */}
					<Stack spacing={2}>
						<Typography variant="subtitle2" color="text.secondary">
							Overflow
						</Typography>
						<FormControl size="small" fullWidth>
							<InputLabel>Overflow</InputLabel>
							<Select label="Overflow" value={localLayout.Overflow ?? ''} onChange={(e) => handleChange('Overflow', e.target.value)}>
								<MenuItem value="">
									<em>None</em>
								</MenuItem>
								{OVERFLOW_OPTIONS.map((option) => (
									<MenuItem key={option} value={option}>
										{option}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Stack direction="row" spacing={2}>
							<FormControl size="small" fullWidth>
								<InputLabel>Overflow X</InputLabel>
								<Select label="Overflow X" value={localLayout.OverflowX ?? ''} onChange={(e) => handleChange('OverflowX', e.target.value)}>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{OVERFLOW_OPTIONS.map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small" fullWidth>
								<InputLabel>Overflow Y</InputLabel>
								<Select label="Overflow Y" value={localLayout.OverflowY ?? ''} onChange={(e) => handleChange('OverflowY', e.target.value)}>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{OVERFLOW_OPTIONS.map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Stack>
					</Stack>
					{/* Alignment Section */}
					<Stack spacing={2}>
						<Typography variant="subtitle2" color="text.secondary">
							Alignment
						</Typography>
						<Stack direction="row" spacing={2}>
							<FormControl size="small" fullWidth>
								<InputLabel>Align Items</InputLabel>
								<Select label="Align Items" value={localLayout.AlignItems ?? ''} onChange={(e) => handleChange('AlignItems', e.target.value)}>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{ALIGN_OPTIONS.map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small" fullWidth>
								<InputLabel>Justify Content</InputLabel>
								<Select label="Justify Content" value={localLayout.JustifyContent ?? ''} onChange={(e) => handleChange('JustifyContent', e.target.value)}>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{JUSTIFY_OPTIONS.map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Stack>
					</Stack>

					{/* Margin Section */}
					<Stack spacing={2}>
						<Typography variant="subtitle2" color="text.secondary">
							Margin
						</Typography>
						<TextField
							label="Margin (All sides)"
							type="number"
							value={localLayout.Margin ?? ''}
							onChange={(e) => handleChange('Margin', e.target.value ? Number(e.target.value) : undefined)}
							size="small"
							fullWidth
							helperText="Sets all margins at once"
						/>
						<Stack direction="row" spacing={2}>
							<TextField
								label="Margin Top"
								type="number"
								value={localLayout.MarginTop ?? ''}
								onChange={(e) => handleChange('MarginTop', e.target.value ? Number(e.target.value) : undefined)}
								size="small"
								fullWidth
							/>
							<TextField
								label="Margin Bottom"
								type="number"
								value={localLayout.MarginBottom ?? ''}
								onChange={(e) => handleChange('MarginBottom', e.target.value ? Number(e.target.value) : undefined)}
								size="small"
								fullWidth
							/>
						</Stack>
						<Stack direction="row" spacing={2}>
							<TextField
								label="Margin Left"
								value={localLayout.MarginLeft ?? ''}
								onChange={(e) => handleChange('MarginLeft', e.target.value === 'auto' ? 'auto' : e.target.value ? Number(e.target.value) : undefined)}
								size="small"
								fullWidth
								placeholder="Number or 'auto'"
							/>
							<TextField
								label="Margin Right"
								value={localLayout.MarginRight ?? ''}
								onChange={(e) => handleChange('MarginRight', e.target.value === 'auto' ? 'auto' : e.target.value ? Number(e.target.value) : undefined)}
								size="small"
								fullWidth
								placeholder="Number or 'auto'"
							/>
						</Stack>
					</Stack>

					{/* Padding Section */}
					<Stack spacing={2}>
						<Typography variant="subtitle2" color="text.secondary">
							Padding
						</Typography>
						<TextField
							label="Padding (All sides)"
							type="number"
							value={localLayout.Padding ?? ''}
							onChange={(e) => handleChange('Padding', e.target.value ? Number(e.target.value) : undefined)}
							size="small"
							fullWidth
							helperText="Sets all paddings at once"
						/>
						<Stack direction="row" spacing={2}>
							<TextField
								label="Padding Top"
								type="number"
								value={localLayout.PaddingTop ?? ''}
								onChange={(e) => handleChange('PaddingTop', e.target.value ? Number(e.target.value) : undefined)}
								size="small"
								fullWidth
							/>
							<TextField
								label="Padding Bottom"
								type="number"
								value={localLayout.PaddingBottom ?? ''}
								onChange={(e) => handleChange('PaddingBottom', e.target.value ? Number(e.target.value) : undefined)}
								size="small"
								fullWidth
							/>
						</Stack>
						<Stack direction="row" spacing={2}>
							<TextField
								label="Padding Left"
								type="number"
								value={localLayout.PaddingLeft ?? ''}
								onChange={(e) => handleChange('PaddingLeft', e.target.value ? Number(e.target.value) : undefined)}
								size="small"
								fullWidth
							/>
							<TextField
								label="Padding Right"
								type="number"
								value={localLayout.PaddingRight ?? ''}
								onChange={(e) => handleChange('PaddingRight', e.target.value ? Number(e.target.value) : undefined)}
								size="small"
								fullWidth
							/>
						</Stack>
					</Stack>

					{/* Border Section */}
					<Stack spacing={2}>
						<Typography variant="subtitle2" color="text.secondary">
							Border
						</Typography>
						<TextField
							label="Border (All sides)"
							value={localLayout.Border ?? ''}
							onChange={(e) => handleChange('Border', e.target.value)}
							size="small"
							fullWidth
							placeholder="e.g. 1px solid black"
							helperText="Shorthand: width style color"
						/>
						<Stack direction="row" spacing={2}>
							<TextField
								label="Border Width"
								value={localLayout.BorderWidth ?? ''}
								onChange={(e) => handleChange('BorderWidth', e.target.value)}
								size="small"
								fullWidth
								placeholder="e.g. 1px, 2"
							/>
							<FormControl size="small" fullWidth>
								<InputLabel>Border Style</InputLabel>
								<Select value={localLayout.BorderStyle ?? ''} onChange={(e) => handleChange('BorderStyle', e.target.value)} label="Border Style">
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{BORDER_STYLE_OPTIONS.map((style) => (
										<MenuItem key={style} value={style}>
											{style}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Stack>
						<Box>
							<FormControlLabel
								control={<Checkbox checked={customBorderColor} onChange={(e) => setCustomBorderColor(e.target.checked)} size="small" />}
								label="Custom border color (hex)"
							/>
							{customBorderColor ? (
								<TextField
									label="Border Color (hex)"
									value={localLayout.BorderColor ?? ''}
									onChange={(e) => handleChange('BorderColor', e.target.value)}
									size="small"
									fullWidth
									placeholder="#000000"
								/>
							) : (
								<Autocomplete
									options={COLOR_OPTIONS}
									value={localLayout.BorderColor ?? ''}
									onChange={(_, value) => handleChange('BorderColor', value)}
									size="small"
									fullWidth
									renderInput={(params) => <TextField {...params} label="Border Color (theme)" placeholder="Select theme color" />}
									renderOption={(props, option) => {
										const resolved = resolveThemeColor(theme, option);
										return (
											<Box component="li" {...props} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
												<Box
													sx={{
														width: 16,
														height: 16,
														borderRadius: 0.5,
														border: '1px solid',
														borderColor: 'divider',
														backgroundColor: resolved || option,
														flexShrink: 0,
													}}
												/>
												<span>{option}</span>
											</Box>
										);
									}}
								/>
							)}
						</Box>
						<TextField
							label="Border Radius"
							value={localLayout.BorderRadius ?? ''}
							onChange={(e) => handleChange('BorderRadius', e.target.value)}
							size="small"
							fullWidth
							placeholder="e.g. 4px, 8"
							helperText="Rounded corners"
						/>
						<Autocomplete
							size="small"
							options={BOX_SHADOW_OPTIONS.map((opt) => opt.value)}
							value={localLayout.BoxShadow ?? ''}
							onChange={(_event, newValue) => handleChange('BoxShadow', newValue ?? undefined)}
							renderInput={(params) => (
								<TextField {...params} label="Box Shadow" placeholder="Select preset or enter custom" helperText="Choose a preset or enter custom shadow" />
							)}
							renderOption={(props, option) => {
								const preset = BOX_SHADOW_OPTIONS.find((opt) => opt.value === option);
								return (
									<Box component="li" {...props}>
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
											<Typography variant="body2">{preset?.label || option}</Typography>
											{preset && (
												<Box
													sx={{
														width: '100%',
														height: 30,
														borderRadius: 0.5,
														border: '1px solid',
														borderColor: 'divider',
														backgroundColor: 'background.paper',
														boxShadow: option,
													}}
												/>
											)}
										</Box>
									</Box>
								);
							}}
							freeSolo
						/>
						<Stack direction="row" spacing={2}>
							<TextField
								label="Border Top"
								value={localLayout.BorderTop ?? ''}
								onChange={(e) => handleChange('BorderTop', e.target.value)}
								size="small"
								fullWidth
								placeholder="e.g. 1px solid black"
							/>
							<TextField
								label="Border Bottom"
								value={localLayout.BorderBottom ?? ''}
								onChange={(e) => handleChange('BorderBottom', e.target.value)}
								size="small"
								fullWidth
								placeholder="e.g. 1px solid black"
							/>
						</Stack>
						<Stack direction="row" spacing={2}>
							<TextField
								label="Border Left"
								value={localLayout.BorderLeft ?? ''}
								onChange={(e) => handleChange('BorderLeft', e.target.value)}
								size="small"
								fullWidth
								placeholder="e.g. 1px solid black"
							/>
							<TextField
								label="Border Right"
								value={localLayout.BorderRight ?? ''}
								onChange={(e) => handleChange('BorderRight', e.target.value)}
								size="small"
								fullWidth
								placeholder="e.g. 1px solid black"
							/>
						</Stack>
					</Stack>

					{/* Text Section */}
					<Stack spacing={2}>
						<Typography variant="subtitle2" color="text.secondary">
							Text
						</Typography>
						<TextField
							label="Font Size"
							value={localLayout.FontSize ?? ''}
							onChange={(e) => handleChange('FontSize', e.target.value)}
							size="small"
							fullWidth
							placeholder="e.g. 14px, 1rem"
						/>
						<Box>
							<Typography variant="caption" color="text.secondary">
								Opacity
							</Typography>
							<Slider
								value={localLayout.Opacity ?? 1}
								onChange={(_e, val) => handleChange('Opacity', val as number)}
								min={0}
								max={1}
								step={0.05}
								valueLabelDisplay="auto"
							/>
						</Box>
						<FormControl size="small" fullWidth>
							<InputLabel>White Space</InputLabel>
							<Select label="White Space" value={localLayout.WhiteSpace ?? ''} onChange={(e) => handleChange('WhiteSpace', e.target.value)}>
								<MenuItem value="">
									<em>None</em>
								</MenuItem>
								{WHITE_SPACE_OPTIONS.map((option) => (
									<MenuItem key={option} value={option}>
										{option}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCancel}>Cancel</Button>
				<Button onClick={handleSave} variant="contained">
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}
